/**
 * Orientation des sprites — **une seule loi, mesurée, pour tout le jeu**.
 *
 * ══ Le défaut que ce module supprime ═════════════════════════════════════════
 *
 * Deux systèmes orientaient des sprites, et tous deux faisaient la même faute : ils
 * passaient l'indice de la DIRECTION VISÉE comme s'il était l'indice de la ROTATION du
 * sprite. Pour tourner un banc vers le sud (côté 2) on chargeait `bench-r2.png` ; pour
 * faire rouler une voiture vers l'est (direction 1) on prenait sa texture d'indice 1.
 *
 * Ce n'est pas ce qu'un quart de tour fait. Mesuré sur `road-end`, dont l'unique branche
 * est une saillie sans ambiguïté (`npm run measure:roads`, contrôle 2) :
 *
 *     road-end.png     branche au SUD    (2)
 *     road-end-r1.png  branche à l'EST   (1)
 *     road-end-r2.png  branche au NORD   (0)
 *     road-end-r3.png  branche à l'OUEST (3)
 *
 * Un quart de tour `-rN` amène donc une saillie de la direction `b` vers `(b − N) mod 4`.
 * Charger `-rD` pour viser la direction `D` obtient `(b − D) mod 4`, et l'application
 * `D ↦ (b − D)` est une **réflexion** : elle coïncide avec l'identité sur exactement deux
 * des quatre directions, **quelle que soit la valeur de `b`**.
 *
 * Autrement dit : **une orientation sur deux était fausse, par construction, et aucun
 * réglage ne pouvait le corriger.** C'est la cause unique du mobilier de travers et des
 * véhicules qui roulent en marche arrière. Les deux points de vue du joueur — « le banc
 * regarde la pelouse » et « la voiture recule » — sont le même défaut.
 *
 * ══ La loi ══════════════════════════════════════════════════════════════════
 *
 *     rotation à charger = (direction de base − direction visée) mod 4
 *
 * Elle est l'inverse d'elle-même, ce qui est vrai de toute réflexion et explique
 * précisément pourquoi la faute passait inaperçue une fois sur deux.
 *
 * ══ Pourquoi `b` ne peut pas être une constante ═════════════════════════════
 *
 * Parce que chaque modèle a la sienne, et qu'elle vient du pack. Mesurée
 * (`npm run measure:facing`) : les quatre véhicules et le lampadaire regardent le SUD à
 * leur rotation 0, le banc regarde le NORD. Une constante unique aurait donc été fausse
 * pour le banc, et la table est générée depuis la mesure plutôt qu'écrite (règle 15).
 */

import { BASE_FACING } from './facing.generated';

/** 0 nord, 1 est, 2 sud, 3 ouest — l'ordre utilisé partout dans le jeu. */
export type Facing = 0 | 1 | 2 | 3;

export function normalizeFacing(value: number): Facing {
  return (((value % 4) + 4) % 4) as Facing;
}

/**
 * Indice de rotation du sprite qui fait regarder `asset` vers `target`.
 *
 * `null` quand l'asset n'a pas d'orientation lisible : un rocher, une poubelle
 * cylindrique, un arbre. L'appelant doit alors utiliser le sprite de base — les tourner
 * au hasard ferait bouger le décor au moindre changement de contexte, pour rien.
 */
export function rotationTowards(asset: string, target: number): number | null {
  const base = BASE_FACING[asset];
  if (base === undefined) return null;
  return normalizeFacing(base - target);
}

/**
 * Nom de sprite orienté, suffixe compris.
 *
 * Centralisé ici parce que la convention « rotation 0 = nom nu » vient du pipeline de
 * rendu (`withOrientations`) et qu'elle était recopiée à trois endroits du moteur.
 */
export function orientedSprite(asset: string, target: number): string {
  const rotation = rotationTowards(asset, target);
  return rotation === null || rotation === 0 ? asset : `${asset}-r${String(rotation)}`;
}

/**
 * Direction vers laquelle regarde effectivement le sprite `-rN` d'un asset.
 *
 * Sert aux tests, et à quiconque doit vérifier la loi dans l'autre sens plutôt que de la
 * relire.
 */
export function facingOfRotation(asset: string, rotation: number): Facing | null {
  const base = BASE_FACING[asset];
  if (base === undefined) return null;
  return normalizeFacing(base - rotation);
}

export { BASE_FACING };
